import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { driveService } from '../services/googleDrive'
import { useSyncStore } from './syncStore'
import { useDataStore } from './dataStore'
import { useGmailStore } from './gmailStore'
import { useAIStore } from './aiStore'

interface User {
  id: string
  email: string
  name: string
  picture?: string
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  accessToken: string | null
  tokenExpiresAt: number | null
  hasGmailScope: boolean
  isLoading: boolean
  isRefreshing: boolean
  error: string | null
  _hasHydrated: boolean
  login: () => Promise<void>
  connectGmail: () => Promise<void>
  disconnectGmail: () => void
  logout: () => void
  refreshToken: () => Promise<boolean>
  restoreSession: () => Promise<void>
  setAuth: (user: User, accessToken: string, hasGmailScope?: boolean) => void
}

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file'
const GMAIL_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly'
const PROFILE_SCOPES = 'email profile'

// Token lifetime: Google access tokens expire after 1 hour (3600 seconds)
// We refresh 5 minutes early to avoid edge cases
const TOKEN_LIFETIME_MS = 55 * 60 * 1000 // 55 minutes
let refreshTimer: ReturnType<typeof setTimeout> | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      tokenExpiresAt: null,
      hasGmailScope: false,
      isLoading: false,
      isRefreshing: false,
      error: null,
      _hasHydrated: false,

      login: async () => {
    set({ isLoading: true, error: null })
    try {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        scope: `${DRIVE_SCOPE} ${GMAIL_SCOPE} ${PROFILE_SCOPES}`,
        callback: async (response) => {
          if (response.error) {
            set({ isLoading: false, error: response.error })
            return
          }
          await fetchUserInfoAndInitDrive(response.access_token, set, get, true)
        },
      })
      client.requestAccessToken()
    } catch (err) {
      set({ 
        isLoading: false, 
        error: err instanceof Error ? err.message : 'Authentication failed' 
      })
    }
  },

  connectGmail: async () => {
    set({ isLoading: true, error: null })
    try {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        scope: `${DRIVE_SCOPE} ${GMAIL_SCOPE} ${PROFILE_SCOPES}`,
        callback: async (response) => {
          if (response.error) {
            set({ isLoading: false, error: response.error })
            return
          }
          await fetchUserInfoAndInitDrive(response.access_token, set, get, true)
        },
      })
      client.requestAccessToken()
    } catch (err) {
      set({ 
        isLoading: false, 
        error: err instanceof Error ? err.message : 'Failed to connect Gmail' 
      })
    }
  },

  disconnectGmail: () => {
    useGmailStore.getState().clearCache()
    set({ hasGmailScope: false })
  },

  logout: () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
    const token = useAuthStore.getState().accessToken
    if (token) {
      google.accounts.oauth2.revoke(token, () => {
        console.log('[ SYSTEM: TOKEN REVOKED. FAREWELL. ]')
      })
    }
    useSyncStore.getState().setConnected(false)
    useSyncStore.getState().setFolderId(null)
    useGmailStore.getState().clearCache()
    set({ 
      isAuthenticated: false, 
      user: null, 
      accessToken: null,
      tokenExpiresAt: null,
      hasGmailScope: false,
      error: null 
    })
  },

  refreshToken: async () => {
    const { hasGmailScope, user } = get()
    if (!user) return false

    set({ isRefreshing: true })
    console.log('[ SYSTEM: REFRESHING ACCESS TOKEN... ]')

    return new Promise<boolean>((resolve) => {
      const scope = hasGmailScope
        ? `${DRIVE_SCOPE} ${GMAIL_SCOPE} ${PROFILE_SCOPES}`
        : `${DRIVE_SCOPE} ${PROFILE_SCOPES}`

      const client = google.accounts.oauth2.initTokenClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        scope,
        callback: async (response) => {
          if (response.error) {
            console.error('[ SYSTEM: TOKEN REFRESH FAILED ]', response.error)
            set({ isRefreshing: false })
            resolve(false)
            return
          }

          const expiresAt = Date.now() + TOKEN_LIFETIME_MS
          set({
            accessToken: response.access_token,
            tokenExpiresAt: expiresAt,
            isRefreshing: false,
          })

          scheduleTokenRefresh(expiresAt, get)
          console.log('[ SYSTEM: TOKEN REFRESHED SUCCESSFULLY ]')
          resolve(true)
        },
        prompt: '', // Silent refresh - no popup if user already consented
      })

      client.requestAccessToken()
    })
  },

  restoreSession: async () => {
    const { user, accessToken, tokenExpiresAt, hasGmailScope } = get()
    
    // No persisted session
    if (!user || !accessToken) {
      set({ _hasHydrated: true })
      return
    }

    set({ isLoading: true })

    // Check if token is expired or expiring soon (within 5 minutes)
    const now = Date.now()
    const tokenExpired = !tokenExpiresAt || tokenExpiresAt < now + (5 * 60 * 1000)

    if (tokenExpired) {
      // Try silent token refresh
      console.log('[ SYSTEM: SESSION EXPIRED, ATTEMPTING SILENT REFRESH... ]')
      
      const scope = hasGmailScope
        ? `${DRIVE_SCOPE} ${GMAIL_SCOPE} ${PROFILE_SCOPES}`
        : `${DRIVE_SCOPE} ${PROFILE_SCOPES}`

      try {
        // Wrap the callback-based API in a Promise
        const newToken = await new Promise<string | null>((resolve) => {
          // Set a timeout in case the callback never fires
          const timeout = setTimeout(() => {
            console.log('[ SYSTEM: SILENT REFRESH TIMED OUT ]')
            resolve(null)
          }, 10000)

          const client = google.accounts.oauth2.initTokenClient({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            scope,
            callback: (response) => {
              clearTimeout(timeout)
              if (response.error) {
                console.log('[ SYSTEM: SILENT REFRESH FAILED ]', response.error)
                resolve(null)
              } else {
                resolve(response.access_token)
              }
            },
            prompt: '', // Silent refresh
          })
          client.requestAccessToken()
        })

        if (newToken) {
          await reinitialiseSession(newToken, user, hasGmailScope, set)
          set({ _hasHydrated: true })
        } else {
          // Silent refresh failed - clear auth state, user needs to log in again
          console.log('[ SYSTEM: SILENT REFRESH FAILED, LOGGING OUT ]')
          set({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            tokenExpiresAt: null,
            hasGmailScope: false,
            isLoading: false,
            _hasHydrated: true,
          })
        }
      } catch (err) {
        console.log('[ SYSTEM: SESSION RESTORATION FAILED ]', err)
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          tokenExpiresAt: null,
          hasGmailScope: false,
          isLoading: false,
          _hasHydrated: true,
        })
      }
    } else {
      // Token still valid, reinitialise services
      console.log('[ SYSTEM: RESTORING SESSION... ]')
      await reinitialiseSession(accessToken, user, hasGmailScope, set)
      scheduleTokenRefresh(tokenExpiresAt, () => useAuthStore.getState())
      set({ _hasHydrated: true })
    }
  },

  setAuth: (user, accessToken, hasGmailScope = false) => {
    const expiresAt = Date.now() + TOKEN_LIFETIME_MS
    set({ 
      isAuthenticated: true, 
      user, 
      accessToken,
      tokenExpiresAt: expiresAt,
      hasGmailScope,
      isLoading: false,
      isRefreshing: false,
      error: null 
    })
    scheduleTokenRefresh(expiresAt, () => useAuthStore.getState())
  },
}),
    {
      name: 'praxis-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        tokenExpiresAt: state.tokenExpiresAt,
        hasGmailScope: state.hasGmailScope,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Trigger session restoration after hydration
        if (state) {
          state.restoreSession()
        }
      },
    }
  )
)

async function reinitialiseSession(
  accessToken: string,
  user: User,
  _hasGmailScope: boolean,
  set: (state: Partial<AuthState>) => void
) {
  try {
    const expiresAt = Date.now() + TOKEN_LIFETIME_MS
    
    // Set state first and ensure it's propagated
    set({
      accessToken,
      tokenExpiresAt: expiresAt,
      isAuthenticated: true,
      isLoading: false,
    })

    // Give Zustand a tick to propagate the state update
    await new Promise(resolve => setTimeout(resolve, 0))

    scheduleTokenRefresh(expiresAt, () => useAuthStore.getState())

    // Verify token is accessible
    const currentToken = useAuthStore.getState().accessToken
    if (!currentToken) {
      throw new Error('Token not available after state update')
    }

    // Reinitialise Drive connection
    console.log('[ SYSTEM: REINITIALISING DRIVE CONNECTION... ]')
    const handshakeSuccess = await driveService.initHandshake()

    if (handshakeSuccess) {
      await useDataStore.getState().hydrate()
      useAIStore.getState().initialise(user.id)
      useSyncStore.getState().setConnected(true)
      console.log('[ SYSTEM: SESSION RESTORED SUCCESSFULLY ]')
    } else {
      console.log('[ SYSTEM: DRIVE HANDSHAKE FAILED ]')
      set({ error: 'Failed to reconnect to Google Drive' })
    }
  } catch (err) {
    console.error('[ SYSTEM: SESSION RESTORATION ERROR ]', err)
    set({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      tokenExpiresAt: null,
      isLoading: false,
      error: 'Session restoration failed',
    })
  }
}

function scheduleTokenRefresh(expiresAt: number, getState: () => AuthState) {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
  }

  const timeUntilRefresh = expiresAt - Date.now() - (5 * 60 * 1000) // Refresh 5 mins before expiry
  
  if (timeUntilRefresh > 0) {
    console.log(`[ SYSTEM: TOKEN REFRESH SCHEDULED IN ${Math.round(timeUntilRefresh / 60000)} MINUTES ]`)
    refreshTimer = setTimeout(async () => {
      const state = getState()
      if (state.isAuthenticated && state.accessToken) {
        await state.refreshToken()
      }
    }, timeUntilRefresh)
  }
}

async function fetchUserInfoAndInitDrive(
  accessToken: string, 
  set: (state: Partial<AuthState>) => void,
  _get: () => AuthState,
  hasGmailScope: boolean
) {
  try {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v2/userinfo',
      { headers: { Authorization: `Bearer ${accessToken}` } }
    )
    if (!response.ok) throw new Error('Failed to fetch user info')
    
    const data = await response.json()
    const expiresAt = Date.now() + TOKEN_LIFETIME_MS
    
    set({
      user: {
        id: data.id,
        email: data.email,
        name: data.name,
        picture: data.picture,
      },
      accessToken,
      tokenExpiresAt: expiresAt,
      hasGmailScope,
    })

    scheduleTokenRefresh(expiresAt, () => useAuthStore.getState())
    
    console.log('[ SYSTEM: AUTHENTICATION COMPLETE. INITIATING DRIVE HANDSHAKE... ]')
    if (hasGmailScope) {
      console.log('[ SYSTEM: GMAIL ACCESS GRANTED. ]')
    }
    
    const handshakeSuccess = await driveService.initHandshake()
    
    if (handshakeSuccess) {
      await useDataStore.getState().hydrate()
      
      // Initialise AI settings
      useAIStore.getState().initialise(data.id)
      
      set({ isAuthenticated: true, isLoading: false })
    } else {
      set({ 
        isLoading: false, 
        error: 'Failed to connect to Google Drive' 
      })
    }
  } catch (err) {
    set({ 
      isLoading: false, 
      error: err instanceof Error ? err.message : 'Failed to fetch user info' 
    })
  }
}

declare global {
  interface Window {
    google: typeof google
  }
  const google: {
    accounts: {
      oauth2: {
        initTokenClient: (config: {
          client_id: string
          scope: string
          callback: (response: { access_token: string; error?: string }) => void
          prompt?: string
        }) => { requestAccessToken: () => void }
        revoke: (token: string, callback: () => void) => void
      }
    }
  }
}
